import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import axios from 'axios';
import { RootState } from '../store';
import { clearCart } from '../store/cartSlice';

// Initialize Stripe
const stripePromise = loadStripe("pk_live_51QlzlICRXun1USEokDZyp5cX1VWoNbj9DZWAm1kHLqt2SG7pVTGeiqhbd2LMJFK2H88OmfCsFkQDjzorPrGt5sEh00svgLdB11");

interface OrderSummaryProps {
  cart: any[];
  discountAmount: number;
  discountPercentage: number;
  referralCode: string;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({ cart, discountAmount, discountPercentage, referralCode }) => {
  const originalTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const finalTotal = originalTotal - discountAmount;

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Order Summary
      </Typography>
      {cart.map((item) => (
        <Box key={item.id} sx={{ mb: 2 }}>
          <Typography variant="body1">
            {item.name} x {item.quantity}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ${(item.price * item.quantity).toFixed(2)}
          </Typography>
        </Box>
      ))}
      <Divider sx={{ my: 2 }} />
      <Box sx={{ mb: 1 }}>
        <Typography variant="body1">
          Subtotal: ${originalTotal.toFixed(2)}
        </Typography>
      </Box>
      
      {discountAmount > 0 && (
        <>
          <Box sx={{ mb: 1 }}>
            <Typography variant="body1" color="success.main">
              Discount ({discountPercentage}% off): -${discountAmount.toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Code: {referralCode}
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
        </>
      )}
      
      <Box>
        <Typography variant="h6">
          Total: ${finalTotal.toFixed(2)}
        </Typography>
        {discountAmount > 0 && (
          <Typography variant="body2" color="success.main">
            You saved ${discountAmount.toFixed(2)}!
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

const CheckoutForm: React.FC<{
  onDiscountUpdate: (discountAmount: number, discountPercentage: number, referralCode: string) => void;
}> = ({ onDiscountUpdate }) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [referralCodeError, setReferralCodeError] = useState('');
  const [referralCodeSuccess, setReferralCodeSuccess] = useState('');
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  
  const cart = useSelector((state: RootState) => state.cart.items);
  const originalTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const finalTotal = originalTotal - discountAmount;

  const validateReferralCode = async (code: string) => {
    if (!code.trim()) {
      setReferralCodeError('');
      setReferralCodeSuccess('');
      setDiscountAmount(0);
      setDiscountPercentage(0);
      onDiscountUpdate(0, 0, '');
      return;
    }

    setIsValidatingCode(true);
    setReferralCodeError('');
    setReferralCodeSuccess('');

    try {
      const response = await axios.post(
        'https://api.missmeg.store/api/validate-referral-code',
        { code: code.trim() },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.valid) {
        const discount = (originalTotal * response.data.discount_percentage) / 100;
        setDiscountAmount(discount);
        setDiscountPercentage(response.data.discount_percentage);
        setReferralCodeSuccess(response.data.message);
        setReferralCodeError('');
        onDiscountUpdate(discount, response.data.discount_percentage, code);
      } else {
        setReferralCodeError(response.data.message);
        setReferralCodeSuccess('');
        setDiscountAmount(0);
        setDiscountPercentage(0);
        onDiscountUpdate(0, 0, '');
      }
    } catch (err) {
      setReferralCodeError('Failed to validate referral code. Please try again.');
      setDiscountAmount(0);
      setDiscountPercentage(0);
      onDiscountUpdate(0, 0, '');
    } finally {
      setIsValidatingCode(false);
    }
  };

  const handleReferralCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const code = event.target.value;
    setReferralCode(code);
    
    // Clear previous validation results
    if (!code.trim()) {
      setReferralCodeError('');
      setReferralCodeSuccess('');
      setDiscountAmount(0);
      setDiscountPercentage(0);
      onDiscountUpdate(0, 0, '');
    }
  };

  const handleReferralCodeBlur = () => {
    if (referralCode.trim()) {
      validateReferralCode(referralCode);
    }
  };

  useEffect(() => {
    // Create PaymentIntent as soon as the page loads or when total changes
    const createPaymentIntent = async () => {
      try {
        const token = localStorage.getItem('token');
        const requestData: any = { 
          amount: originalTotal
        };
        
        // Only add referral_code if it's not empty
        if (referralCode.trim()) {
          requestData.referral_code = referralCode.trim();
        }
        
        const response = await axios.post(
          'https://api.missmeg.store/api/create-payment-intent',
          requestData,
          {
            headers: {
              "Authorization": "Bearer " + token,
              'Content-Type': 'application/json',
            },
          }
        );
        setClientSecret(response.data.clientSecret);
      } catch (err) {
        console.error('Payment intent error:', err);
        setError('Failed to initialize payment. Please try again.');
      }
    };

    if (originalTotal > 0) {
      createPaymentIntent();
    }
  }, [originalTotal, referralCode]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    try {
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement)!,
          },
        }
      );

      if (stripeError) {
        setError(stripeError.message || 'An error occurred');
      } else if (paymentIntent.status === 'succeeded') {
        // Create order
        const orderData: any = {
          items: cart.map(item => ({
            product_id: item.id,
            quantity: item.quantity,
            price: item.price,
          })),
          total_amount: finalTotal,
          discount_amount: discountAmount
        };
        
        // Only add referral_code if it's not empty
        if (referralCode.trim()) {
          orderData.referral_code = referralCode.trim();
        }
        
        console.log('=== FRONTEND ORDER DATA ===');
        console.log('Order data being sent:', orderData);
        console.log('Cart items:', cart);
        console.log('Final total:', finalTotal);
        console.log('Discount amount:', discountAmount);
        console.log('Referral code:', referralCode);
        
        try {
          const response = await axios.post(
            'https://api.missmeg.store/api/orders',
            orderData,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
              },
            }
          );
          
          console.log('Order creation response:', response.data);
          dispatch(clearCart());
          navigate('/order-success');
        } catch (orderError: any) {
          console.error('Order creation error:', orderError);
          console.error('Order error response:', orderError.response?.data);
          setError(`Order creation failed: ${orderError.response?.data?.error || orderError.message}`);
        }
      }
    } catch (err) {
      console.error('Payment processing error:', err);
      setError('An error occurred while processing your payment.');
    }

    setProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Referral Code (Optional)
        </Typography>
        <TextField
          fullWidth
          label="Enter referral code"
          value={referralCode}
          onChange={handleReferralCodeChange}
          onBlur={handleReferralCodeBlur}
          disabled={isValidatingCode}
          error={!!referralCodeError}
          helperText={referralCodeError || referralCodeSuccess}
          sx={{ mb: 2 }}
        />
        {isValidatingCode && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography variant="body2">Validating code...</Typography>
          </Box>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Payment Details
        </Typography>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={!stripe || processing}
      >
        {processing ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          `Pay $${finalTotal.toFixed(2)}`
        )}
      </Button>
    </form>
  );
};

const Checkout: React.FC = () => {
  const cart = useSelector((state: RootState) => state.cart.items);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [referralCode, setReferralCode] = useState('');

  const handleDiscountUpdate = (newDiscountAmount: number, newDiscountPercentage: number, newReferralCode: string) => {
    setDiscountAmount(newDiscountAmount);
    setDiscountPercentage(newDiscountPercentage);
    setReferralCode(newReferralCode);
  };

  if (cart.length === 0) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Your cart is empty
          </Typography>
          <Button variant="contained" href="/" sx={{ mt: 2 }}>
            Continue Shopping
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Checkout
            </Typography>
            <Elements stripe={stripePromise}>
              <CheckoutForm onDiscountUpdate={handleDiscountUpdate} />
            </Elements>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <OrderSummary 
            cart={cart} 
            discountAmount={discountAmount} 
            discountPercentage={discountPercentage} 
            referralCode={referralCode} 
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default Checkout; 